/**
 * Node Hash Algorithm
 *
 * Generates deterministic hashes for workflow nodes based on their semantic content.
 * Used for cache lookup - nodes with the same hash produce the same behavior.
 *
 * This module holds the pure hash algorithm (`NodeHashInput` → hash string).
 * Extracting a `NodeHashInput` from a live `WorkflowNodeContext` is a server
 * concern and lives in `apps/api/src/workflows/helpers/node-hash.ts`.
 *
 * Hash versions:
 * - v1: Initial version (2026-01-15) - proper deep key sorting
 * - v2 (current default): Added `playbookSteps` (order+action+target) to
 *   input for action/assertion nodes.
 * - v3 (per-org, behind `workflow.recipe-v3`): pure node identity — drops
 *   `playbookSteps` (the playbook is no longer part of its own cache key; it
 *   rewrites in place on the identity row) and drops the vestigial
 *   `inlineAssertions[].severity` (folding its deferred removal into a
 *   re-mint that is already happening — see the NodeHashInput comment).
 *   Selected per-org at the callsites via `generateNodeHashV3`, NOT a global
 *   `HASH_VERSION` bump — a global bump would invalidate every org's cache to
 *   roll out a per-org flag.
 *
 * Note: Changing the hash algorithm will cause existing caches to miss.
 * This is acceptable - caches will repopulate on next execution.
 *
 * @module
 * @see docs/features/workflow-cache.md for caching design
 */

import { createHash } from 'crypto';

/** Current hash version - increment if hash algorithm changes */
const HASH_VERSION = 'v2';

/**
 * Recursively sort object keys for deterministic JSON serialization.
 * Handles nested objects and arrays.
 */
export function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }
  if (obj !== null && typeof obj === 'object') {
    const sorted = Object.keys(obj as Record<string, unknown>)
      .sort()
      .reduce(
        (acc, key) => {
          acc[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
          return acc;
        },
        {} as Record<string, unknown>
      );
    return sorted;
  }
  return obj;
}

/**
 * Input data for generating a node hash.
 * Only includes fields that affect node behavior (excludes cosmetic fields).
 */
export interface NodeHashInput {
  nodeType: string;
  /** Primary description/condition based on node type */
  description?: string;
  /** Action node title (separate from description for stable hashing) */
  title?: string | null;
  /** URL the node operates on */
  pageUrl?: string;
  /** Data requirements for action nodes (name + type) */
  dataRequirements?: Array<{ name: string; type?: string }>;
  /** Playbook steps for playbook-capable nodes (order + action + target) — cached actions are replayed 1:1 against these, so a mismatch must miss */
  playbookSteps?: Array<{ order: number; action: string; target: string }>;
  /**
   * Inline assertions for action nodes (`id` excluded — random UUID would cause
   * spurious invalidation).
   *
   * `severity` is vestigial here: replay resolves it authoritatively from current
   * node config (cache/replay.ts), treating the cached value as advisory, so it
   * does not affect replayed behavior and ideally would not be hashed. It is kept
   * only because dropping it standalone re-mints ~29% of action caches for no
   * behavioral gain. If the hash algorithm ever changes for another reason, drop
   * `severity` then too — folding the re-mint into one that is already happening.
   */
  inlineAssertions?: Array<{ condition: string; severity: string; agentic?: boolean }>;
}

/**
 * Generate a deterministic hash for a node based on its semantic content.
 * Uses SHA-256 truncated to 16 characters with a version prefix.
 *
 * Format: "v2:abc123..." (version prefix + 16 hex characters)
 *
 * @param input - Node hash input (from extractHashInput or constructed manually)
 * @returns Deterministic hash string with version prefix
 */
export function generateNodeHash(input: NodeHashInput): string {
  return hashWithPrefix(input, HASH_VERSION);
}

/**
 * v3: pure node-identity hash (per-org, behind `workflow.recipe-v3`).
 *
 * Accepts the same `NodeHashInput` as v2 and drops the non-identity fields
 * itself, so callers don't need a parallel extraction path:
 *   - `playbookSteps` — derived content; lives on the identity row and
 *     updates in place rather than moving the row.
 *   - `inlineAssertions[].severity` — replay resolves severity from current
 *     node config; hashing it only caused spurious re-mints.
 */
export function generateNodeHashV3(input: NodeHashInput): string {
  const { playbookSteps: _playbookSteps, inlineAssertions, ...identity } = input;
  const v3Input: Omit<NodeHashInput, 'playbookSteps' | 'inlineAssertions'> & {
    inlineAssertions?: Array<{ condition: string; agentic?: boolean }>;
  } = {
    ...identity,
    ...(inlineAssertions
      ? {
          inlineAssertions: inlineAssertions.map(({ condition, agentic }) => ({
            condition,
            ...(agentic !== undefined ? { agentic } : {}),
          })),
        }
      : {}),
  };
  return hashWithPrefix(v3Input, 'v3');
}

function hashWithPrefix(input: unknown, version: string): string {
  // Deep-sort all keys for consistent serialization regardless of property order
  const sortedInput = sortObjectKeys(input);
  const stableJson = JSON.stringify(sortedInput);

  // Generate SHA-256 hash, truncate to 16 chars for reasonable uniqueness
  const hash = createHash('sha256').update(stableJson).digest('hex').slice(0, 16);

  // Include version prefix for future algorithm changes
  return `${version}:${hash}`;
}
